import { Component, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { RouteKeys } from 'src/app/share/helper/route-keys.helper';
import { ApiService } from 'src/app/share/services/api.service';
import { PopupService } from '../../services/popup.service';
import { lastValueFrom, Subscription } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import notify from 'devextreme/ui/notify';

interface StapeldruckRequest {
  entityIds: number[];
  entityArt: string;
  vorlagenId: number;
  fileName: string;
  ausgabeFormat: string;
  anheften: boolean;
}

interface StapeldruckDokument {
  entityId: number;
  kennzeichen: string;
  dokumentId: number | null;
  fileName: string;
}

interface StapeldruckFehler {
  entityId: number;
  kennzeichen: string;
  fehler: string;
}

interface StapeldruckResponse {
  fileName: string;
  mimeType: string;
  dokumentArt: string;
  dokumente: StapeldruckDokument[];
  fehler: StapeldruckFehler[];
}

interface StapeldruckEntityKonfiguration {
  bezeichnung: string;
  mehrzahl: string;
  ablageDativ: string;
}

const docxMimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const pdfMimeType = 'application/pdf';

const ausgabeFormatKonfiguration: Record<string, { extension: string; mimeType: string }> = {
  Pdf: { extension: 'pdf', mimeType: pdfMimeType },
  Docx: { extension: 'docx', mimeType: docxMimeType },
};

const stapeldruckEntityKonfiguration: Record<string, StapeldruckEntityKonfiguration> = {
  antrag: { bezeichnung: 'Antrag', mehrzahl: 'Anträge', ablageDativ: 'Antrag' },
  vertrag: { bezeichnung: 'Vertrag', mehrzahl: 'Verträge', ablageDativ: 'Vertrag' },
  haushalt: { bezeichnung: 'Haushalt', mehrzahl: 'Haushalte', ablageDativ: 'Haushalt' },
};

@Component({
  selector: 'app-vorlagenPopup',
  templateUrl: './vorlagenPopup.component.html',
  styleUrls: ['./vorlagenPopup.component.scss'],
  standalone: false,
})
export class VorlagenPopupComponent implements OnInit, OnDestroy {
  @ViewChild('popup', { static: false }) popup: DxPopupComponent;
  @ViewChild('vorlagenDataGrid', { static: false }) vorlagenDataGrid: DxDataGridComponent;
  @ViewChild('form', { static: false }) form: DxFormComponent;

  isLoading: boolean = false;
  onClose: (e, data) => void;

  formData: any = {
    entityTyp: '',
    bezeichnung: '',
    anheften: true,
    ausgabeFormat: 'Pdf',
  };
  dataSource: DataSource;

  ausgabeFormate = [
    { id: 'Pdf', text: 'PDF (UA)' },
    { id: 'Docx', text: 'DOCX' },
  ];
  anheftOptionen = [
    { id: true, text: 'Ja' },
    { id: false, text: 'Nein' },
  ];

  id: number;
  ids: number[] = [];
  seriendruckFehler: StapeldruckFehler[] = [];
  seriendruckAbgeschlossen: boolean = false;
  stapeldruckPreviewDokumente: any[] = [];
  stapeldruckPreviewDokumentArt: string = null;
  blob: Blob;
  vorlagen: any[] = [];
  selectedItem: any = null;
  entityTyp: string;
  private letzteErstellteVorlagenId: number | null = null;

  subscription_get_PopupAsObservable: Subscription;
  private readonly stapeldruckNotifyDuration: number = 8000;

  constructor(
    private apiService: ApiService,
    private popupService: PopupService
  ) {
    this.onClickClose = this.onClickClose.bind(this);
    this.onClickErzeugen = this.onClickErzeugen.bind(this);
    this.onClickFehlerlisteDownload = this.onClickFehlerlisteDownload.bind(this);
    this.onClickStapeldruckPreview = this.onClickStapeldruckPreview.bind(this);
  }

  ngOnInit(): void {
    this.subscription_get_PopupAsObservable = this.popupService.get_vorlagePopupAsObservable.subscribe((context) => {
      if (context !== null && context !== undefined && context.open === true) {
        this.formData = {
          entityArt: context.entityArt,
          bezeichnung: '',
          anheften: true,
          ausgabeFormat: 'Pdf',
        };
        this.onClose = context.closeCb;
        this.selectedItem = null;
        this.id = context.key;
        this.ids = context.keys ?? [context.key];
        this.seriendruckFehler = [];
        this.seriendruckAbgeschlossen = false;
        this.stapeldruckPreviewDokumente = [];
        this.stapeldruckPreviewDokumentArt = null;
        this.letzteErstellteVorlagenId = null;
        this.dataSource.pageIndex(0);
        this.vorlagenDataGrid?.instance.clearSelection();
        this.vorlagenDataGrid?.instance.getScrollable()?.scrollTo({ top: 0, left: 0 });
        this.dataSource.reload();
        this.popup.instance.show();
      }
    });

    this.dataSource = new DataSource({
      key: 'vorlagenId',
      load: (loadOptions) => {
        loadOptions.userData.selector = this.formData.entityArt;
        return lastValueFrom(this.apiService.post<any>(RouteKeys.Vorlage.get_search, loadOptions))
          .then((response) => {
            return {
              data: response.data,
              totalCount: response.totalCount,
              summary: response.summary,
              groupCount: response.groupCount,
            };
          })
          .catch((err) => {
            throw err.message;
          });
      },
    });
  }

  ngOnDestroy(): void {
    if (this.subscription_get_PopupAsObservable) {
      this.subscription_get_PopupAsObservable.unsubscribe();
    }
  }

  onContentReady(e) {
    e.component.columnOption('command:select', {
      visibleIndex: 1,
      fixedPosition: 'right',
      visibleWidth: 70,
    });

    e.component.getScrollable()?.update();
  }

  onPopupShown(): void {
    const dataGrid = this.vorlagenDataGrid?.instance;
    if (!dataGrid) {
      return;
    }

    dataGrid.updateDimensions();
    const scrollable = dataGrid.getScrollable();
    scrollable?.scrollTo({ top: 0, left: 0 });
    scrollable?.update();
  }

  onToolbarPreparing(e) {
    var searchPanel = e.toolbarOptions.items.filter((item) => item.name === 'searchPanel');
    if (searchPanel && searchPanel[0]) {
      searchPanel[0].location = 'before';
    }
  }

  onSelectionChanged(e) {
    this.selectedItem = e.selectedRowsData[0];

    if (this.selectedItem !== undefined && this.selectedItem !== null) {
      if (this.isStapeldruck() && this.seriendruckAbgeschlossen && this.selectedItem.vorlagenId !== this.letzteErstellteVorlagenId) {
        this.seriendruckAbgeschlossen = false;
      }

      const bezeichnung = this.form.instance.getEditor('bezeichnung').option('value');
      const newFileName = this.selectedItem.fileName?.split('.')[0];

      if (bezeichnung === undefined || bezeichnung === null || bezeichnung.length === 0 || bezeichnung !== newFileName) {
        this.form.instance.getEditor('bezeichnung').option('value', newFileName);
      }
    }
  }

  onStapeldruckOptionChanged(e): void {
    if (this.isStapeldruck() && this.seriendruckAbgeschlossen && e.value !== e.previousValue) {
      this.seriendruckAbgeschlossen = false;
    }
  }

  onClickErzeugen(e) {
    this.isLoading = true;
    var validationResult = this.form.instance.validate();
    if (validationResult.isValid) {
      if (this.isStapeldruck()) {
        this.seriendruckFehler = [];
        this.stapeldruckPreviewDokumente = [];
        this.stapeldruckPreviewDokumentArt = null;
        this.popupService.clearDokumentPreviewPopup();
        const request: StapeldruckRequest = {
          entityIds: this.ids,
          entityArt: this.formData.entityArt,
          vorlagenId: this.selectedItem.vorlagenId,
          fileName: this.formData.bezeichnung,
          ausgabeFormat: this.formData.ausgabeFormat,
          anheften: this.formData.anheften,
        };

        this.apiService.post<StapeldruckResponse>(RouteKeys.Vorlage.create_stapeldruck, request).subscribe({
          next: (response: StapeldruckResponse) => {
            this.seriendruckFehler = response.fehler ?? [];
            const dokumente: StapeldruckDokument[] = response.dokumente ?? [];
            const previewDokumente: any[] = dokumente.map((dokument: StapeldruckDokument) => ({
                dokumentId: dokument.dokumentId,
                fileName: dokument.fileName,
                mimeType: response.mimeType,
                aktenzeichen: dokument.kennzeichen,
                bezeichnung: dokument.kennzeichen
                  ? `${this.getEntityArtBezeichnung()} ${dokument.kennzeichen}`
                  : `${this.getEntityArtBezeichnung()} ID ${dokument.entityId}`,
                previewRequest:
                  dokument.dokumentId === null
                    ? {
                        ...request,
                        entityIds: [dokument.entityId],
                        anheften: false,
                      }
                    : null,
              }));

            if (dokumente.length === 0) {
              this.showStapeldruckResult(response, previewDokumente, true);
              return;
            }

            const gesamtdokumentRequest: StapeldruckRequest = {
              ...request,
              entityIds: dokumente.map((dokument: StapeldruckDokument) => dokument.entityId),
            };

            this.apiService.postDownloadAsBlob(
              RouteKeys.Vorlage.create_stapeldruck_gesamtdokument,
              response.fileName,
              gesamtdokumentRequest,
              (blob: Blob) => {
                previewDokumente.unshift({
                  blob: blob,
                  fileName: response.fileName,
                  mimeType: response.mimeType,
                  bezeichnung: 'Gesamtdokument',
                });
                this.showStapeldruckResult(response, previewDokumente, true);
              },
              (error) => {
                if (error) {
                  this.showStapeldruckResult(response, previewDokumente, false);
                }
              }
            );
          },
          error: (error) => {
            this.isLoading = false;
            notify(
              { message: error?.message || 'Der Seriendruck konnte nicht erstellt werden.' },
              'error',
              this.stapeldruckNotifyDuration
            );
          },
        });
        return;
      }

      this.createEinzeldruck();
    } else {
      this.isLoading = false;
    }
  }

  private showStapeldruckResult(response: StapeldruckResponse, previewDokumente: any[], gesamtdokumentErstellt: boolean): void {
    this.isLoading = false;
    this.letzteErstellteVorlagenId = this.selectedItem?.vorlagenId ?? null;
    const anzahlErfolgreich = response.dokumente?.length ?? 0;

    if (previewDokumente.length > 0) {
      this.stapeldruckPreviewDokumente = previewDokumente;
      this.stapeldruckPreviewDokumentArt = response.dokumentArt;
      this.openDokumentPreview(previewDokumente, response.dokumentArt);
    }

    if (!gesamtdokumentErstellt) {
      this.seriendruckAbgeschlossen = true;
      notify(
        { message: `${anzahlErfolgreich} Einzeldokumente wurden erstellt. Das Gesamtdokument konnte nicht geladen werden.` },
        'error',
        this.stapeldruckNotifyDuration
      );
      return;
    }

    if (this.seriendruckFehler.length > 0) {
      this.seriendruckAbgeschlossen = true;
      notify(
        {
          message: `${anzahlErfolgreich} Dokumente wurden erstellt. ${this.seriendruckFehler.length} Datensätze konnten nicht bearbeitet werden.`,
        },
        'warning',
        this.stapeldruckNotifyDuration
      );
      return;
    }

    this.seriendruckAbgeschlossen = true;
    const message = this.formData.anheften
      ? `${anzahlErfolgreich} Dokumente wurden erstellt und angeheftet.`
      : `${anzahlErfolgreich} Dokumente wurden erstellt.`;
    notify({ message }, 'success', this.stapeldruckNotifyDuration);
  }

  onClickStapeldruckPreview(): void {
    if (this.stapeldruckPreviewDokumente.length > 0) {
      this.openDokumentPreview(this.stapeldruckPreviewDokumente, this.stapeldruckPreviewDokumentArt);
    }
  }

  private createEinzeldruck(): void {
    const isDocx = this.formData.ausgabeFormat === 'Docx';
    const formatKonfiguration = ausgabeFormatKonfiguration[this.formData.ausgabeFormat] ?? ausgabeFormatKonfiguration.Pdf;
    const request = {
      entityId: this.id,
      vorlagenId: this.selectedItem.vorlagenId,
      anheften: this.formData.anheften,
      fileName: this.formData.bezeichnung,
      bearbeiten: isDocx,
      ausgabeFormat: this.formData.ausgabeFormat,
      entityArt: this.formData.entityArt,
    };

    if (this.formData.anheften) {
      this.apiService.post(RouteKeys.Vorlage.create_without_download, request).subscribe({
        next: (response) => {
          this.isLoading = false;
          this.popup.instance.hide();
          if (response && this.onClose !== undefined && this.onClose !== null) {
            this.onClose(null, true);
          }
        },
        error: (error) => {
          this.isLoading = false;
          notify({ message: error?.message || 'Das Dokument konnte nicht erstellt werden.' }, 'error');
        },
      });
      return;
    }

    const mimeType = formatKonfiguration.mimeType;
    const fileName = `${this.formData.bezeichnung}.${formatKonfiguration.extension}`;
    this.apiService.postDownloadAsBlob(
      RouteKeys.Vorlage.create,
      fileName,
      request,
      (blob: Blob) => {
        this.isLoading = false;
        this.popup.instance.hide();
        this.openDokumentPreview(
          [
            {
              blob: blob,
              fileName: fileName,
              mimeType: mimeType,
              bezeichnung: this.formData.bezeichnung,
            },
          ],
          this.formData.entityArt,
          true
        );
      },
      (error) => {
        if (error) {
          this.isLoading = false;
          notify({ message: 'Das Dokument konnte nicht erstellt werden.' }, 'error');
        }
      }
    );
  }

  private openDokumentPreview(
    dokumente: any[],
    dokumentArt: string,
    bearbeitungErlaubt = false,
    saveCallback: ((content: any) => void) | null = null
  ): void {
    const istBearbeitbaresFormat = dokumente.some((dokument) => dokument.mimeType === docxMimeType);
    const istBearbeitbar = bearbeitungErlaubt && istBearbeitbaresFormat;
    this.popupService.showDokumentPreviewPopup(
      dokumente,
      0,
      dokumentArt,
      !istBearbeitbar,
      istBearbeitbar ? saveCallback ?? ((content) => this.downloadEditedDocx(content)) : null
    );
  }

  private downloadEditedDocx(content: any): void {
    if (!content?.docContentBase64) {
      return;
    }

    const binaryContent = window.atob(content.docContentBase64);
    const bytes = new Uint8Array(binaryContent.length);
    for (let index = 0; index < binaryContent.length; index++) {
      bytes[index] = binaryContent.charCodeAt(index);
    }

    const blob = new Blob([bytes], { type: docxMimeType });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = content.docName?.toLowerCase().endsWith('.docx') ? content.docName : `${content.docName || 'Dokument'}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 800);
  }

  async onClickFehlerlisteDownload(): Promise<void> {
    if (this.seriendruckFehler.length === 0) {
      return;
    }

    const [{ jsPDF }, { autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const erstelltAm = new Date();
    const zeitstempel = new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    }).format(erstelltAm);
    const dokument = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    dokument.setFontSize(16);
    dokument.text('Fehlerliste Stapeldruck', 14, 18);
    dokument.setFontSize(10);
    dokument.text(`Zeitstempel: ${zeitstempel}`, 14, 26);
    dokument.text(`Anzahl der Fehler: ${this.seriendruckFehler.length}`, 14, 32);

    autoTable(dokument, {
      startY: 38,
      head: [['Nr.', this.getEntityArtBezeichnung(), 'Fehler']],
      body: this.seriendruckFehler.map((fehler: StapeldruckFehler, index: number) => [
        (index + 1).toString(),
        this.getFehlerKennzeichen(fehler),
        fehler.fehler,
      ]),
      theme: 'striped',
      styles: {
        fontSize: 9,
        cellPadding: 2.5,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [51, 122, 183],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'right' },
        1: { cellWidth: 45 },
        2: { cellWidth: 'auto' },
      },
      margin: { top: 14, right: 14, bottom: 18, left: 14 },
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      didDrawPage: () => {
        const seitenBreite = dokument.internal.pageSize.getWidth();
        const seitenHoehe = dokument.internal.pageSize.getHeight();
        const seitenNummer = dokument.getCurrentPageInfo().pageNumber;

        dokument.setFontSize(8);
        dokument.setTextColor(100);
        dokument.text(`Erstellt: ${zeitstempel}`, 14, seitenHoehe - 8);
        dokument.text(`Seite ${seitenNummer}`, seitenBreite - 14, seitenHoehe - 8, { align: 'right' });
        dokument.setTextColor(0);
      },
    });

    const dateiZeitstempel = [
      erstelltAm.getFullYear(),
      (erstelltAm.getMonth() + 1).toString().padStart(2, '0'),
      erstelltAm.getDate().toString().padStart(2, '0'),
      '_',
      erstelltAm.getHours().toString().padStart(2, '0'),
      '-',
      erstelltAm.getMinutes().toString().padStart(2, '0'),
      '-',
      erstelltAm.getSeconds().toString().padStart(2, '0'),
    ].join('');

    dokument.save(`Fehlerliste_Stapeldruck_${dateiZeitstempel}.pdf`);
  }

  getEntityArtBezeichnung(): string {
    return this.getEntityKonfiguration().bezeichnung;
  }

  getEntityArtMehrzahl(): string {
    return this.getEntityKonfiguration().mehrzahl;
  }

  getFehlerKennzeichen(fehler: StapeldruckFehler): string {
    if (!fehler.kennzeichen) {
      return `Unbekannter ${this.getEntityArtBezeichnung()}`;
    }

    if (this.formData.entityArt === 'antrag' || this.formData.entityArt === 'vertrag') {
      return `AZ: ${fehler.kennzeichen}`;
    }

    if (this.formData.entityArt === 'haushalt') {
      return `KZ: ${fehler.kennzeichen}`;
    }

    return fehler.kennzeichen;
  }

  isStapeldruck(): boolean {
    return this.ids.length > 1;
  }

  private getEntityKonfiguration(entityArt: string = this.formData.entityArt): StapeldruckEntityKonfiguration {
    return (
      stapeldruckEntityKonfiguration[entityArt] ?? {
        bezeichnung: 'Datensatz',
        mehrzahl: 'Datensätze',
        ablageDativ: 'Datensatz',
      }
    );
  }

  onClickClose(e) {
    this.stapeldruckPreviewDokumente = [];
    this.stapeldruckPreviewDokumentArt = null;
    this.popupService.clearDokumentPreviewPopup();
    this.popup.instance.hide();
  }

}
